import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { DialogFooter } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useFetchAccounts } from '@accountHooks';
import { useRecordStagePayment } from '@/hooks/useLegalFeeStages';
import { stagePaymentFormSchema, type StagePaymentFormData } from './legalFees.schema';
import { formatCurrency } from '@/lib/currency.utils';
import type { LegalFeeStage } from '@/types/legalFees';

interface StagePaymentFormProps {
  stage: LegalFeeStage;
  fixedAssetId: string;
  propertyName: string;
  assetCurrency: string;
  onSuccess: () => void;
}

/**
 * Form for bulk-paying all unpaid items in a stage with a single transaction.
 * Validates total_amount ≤ unpaid sum at runtime (Req 6.2).
 * Does NOT modify fixed_assets.amount_paid (Req 8.1).
 */
export function StagePaymentForm({
  stage,
  fixedAssetId,
  propertyName,
  assetCurrency,
  onSuccess,
}: StagePaymentFormProps) {
  const { data: accounts = [], isLoading: accountsLoading } = useFetchAccounts();
  const recordStagePayment = useRecordStagePayment();

  const today = new Date().toISOString().split('T')[0];

  const unpaidItems = stage.items.filter((item) => item.paid_status === 'unpaid');
  const unpaidSum = unpaidItems.reduce((sum, item) => sum + item.amount, 0);

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    setError,
    formState: { errors, isSubmitting },
  } = useForm<StagePaymentFormData>({
    resolver: zodResolver(stagePaymentFormSchema),
    defaultValues: {
      account_id: '',
      total_amount: unpaidSum,
      payment_date: today,
      notes: '',
    },
  });

  const accountId = watch('account_id');
  const selectedAccount = accounts.find((a) => a.id === accountId);

  const onSubmit = async (data: StagePaymentFormData) => {
    // Runtime validation: total_amount must not exceed the unpaid sum (Req 6.2)
    if (data.total_amount > unpaidSum) {
      setError('total_amount', {
        message: `Amount cannot exceed the unpaid total of ${formatCurrency(unpaidSum, assetCurrency)}`,
      });
      return;
    }

    try {
      await recordStagePayment.mutateAsync({
        stageId: stage.id,
        fixedAssetId,
        accountId: data.account_id,
        totalAmount: data.total_amount,
        paymentDate: data.payment_date,
        notes: data.notes ?? null,
        paymentCurrency: assetCurrency,
        stageName: stage.name,
        propertyName,
      });
      toast.success('Stage payment recorded successfully');
      onSuccess();
    } catch (error: any) {
      // Surface InsufficientBalanceError and other RPC errors via toast
      toast.error(error?.message ?? 'Failed to record stage payment');
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
      {/* Unpaid item breakdown (Req 6.1) */}
      <div className="rounded-md border p-3 space-y-2 bg-muted/30">
        <p className="text-sm font-medium text-foreground">Unpaid items</p>
        {unpaidItems.length === 0 ? (
          <p className="text-sm text-muted-foreground">No unpaid items in this stage.</p>
        ) : (
          <ul className="space-y-1" aria-label="Unpaid items breakdown">
            {unpaidItems.map((item) => (
              <li key={item.id} className="flex justify-between text-sm">
                <span className="text-muted-foreground">{item.title}</span>
                <span className="font-medium tabular-nums">
                  {formatCurrency(item.amount, item.currency)}
                </span>
              </li>
            ))}
          </ul>
        )}
        <div className="flex justify-between text-sm font-semibold border-t pt-2 mt-2">
          <span>Total</span>
          <span className="tabular-nums">{formatCurrency(unpaidSum, assetCurrency)}</span>
        </div>
      </div>

      {/* Total amount — pre-filled, editable, capped at unpaid sum */}
      <div className="space-y-2">
        <Label htmlFor="stage-payment-total">Total Amount *</Label>
        <Input
          id="stage-payment-total"
          type="number"
          step="0.01"
          min="0.01"
          max={unpaidSum}
          {...register('total_amount', { valueAsNumber: true })}
          aria-invalid={!!errors.total_amount}
          aria-describedby={errors.total_amount ? 'stage-payment-total-error' : undefined}
        />
        {errors.total_amount && (
          <p id="stage-payment-total-error" className="text-sm text-destructive" role="alert">
            {errors.total_amount.message}
          </p>
        )}
      </div>

      {/* Account selector */}
      <div className="space-y-2">
        <Label htmlFor="stage-payment-account">Debit Account *</Label>
        <Select
          value={accountId}
          onValueChange={(val) => setValue('account_id', val)}
          disabled={accountsLoading}
        >
          <SelectTrigger
            id="stage-payment-account"
            aria-invalid={!!errors.account_id}
            aria-describedby={errors.account_id ? 'stage-payment-account-error' : undefined}
          >
            <SelectValue placeholder="Select an account" />
          </SelectTrigger>
          <SelectContent>
            {accounts.map((acc) => (
              <SelectItem key={acc.id} value={acc.id}>
                {acc.nickname || acc.name} ({acc.currency})
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {errors.account_id && (
          <p id="stage-payment-account-error" className="text-sm text-destructive" role="alert">
            {errors.account_id.message}
          </p>
        )}
        {selectedAccount && (
          <p className="text-xs text-muted-foreground">
            Balance: {formatCurrency(selectedAccount.balance, selectedAccount.currency)}
          </p>
        )}
      </div>

      {/* Date picker — no future dates */}
      <div className="space-y-2">
        <Label htmlFor="stage-payment-date">Payment Date *</Label>
        <Input
          id="stage-payment-date"
          type="date"
          max={today}
          {...register('payment_date')}
          aria-invalid={!!errors.payment_date}
          aria-describedby={errors.payment_date ? 'stage-payment-date-error' : undefined}
        />
        {errors.payment_date && (
          <p id="stage-payment-date-error" className="text-sm text-destructive" role="alert">
            {errors.payment_date.message}
          </p>
        )}
      </div>

      {/* Notes */}
      <div className="space-y-2">
        <Label htmlFor="stage-payment-notes">Notes (Optional)</Label>
        <Textarea
          id="stage-payment-notes"
          {...register('notes')}
          placeholder="Optional notes…"
          rows={2}
          maxLength={500}
        />
        {errors.notes && (
          <p className="text-sm text-destructive" role="alert">
            {errors.notes.message}
          </p>
        )}
      </div>

      <DialogFooter>
        <Button type="submit" disabled={isSubmitting || recordStagePayment.isPending || unpaidItems.length === 0}>
          {isSubmitting || recordStagePayment.isPending ? 'Processing…' : 'Record Stage Payment'}
        </Button>
      </DialogFooter>
    </form>
  );
}
